import { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { RotateCcw, Bot, User } from "lucide-react";
import axios from "axios";
import { useLang, STR } from "@/i18n";
import { sfx } from "@/sound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINS = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8],
  [0, 3, 6], [1, 4, 7], [2, 5, 8],
  [0, 4, 8], [2, 4, 6],
];

function winnerOf(b) {
  for (const [a, c, d] of WINS) {
    if (b[a] && b[a] === b[c] && b[a] === b[d]) return b[a];
  }
  return b.every(Boolean) ? "draw" : null;
}

function aiMove(b) {
  const empty = b.map((v, i) => (v ? null : i)).filter((v) => v !== null);
  for (const i of empty) {
    const t = [...b];
    t[i] = "O";
    if (winnerOf(t) === "O") return i;
  }
  for (const i of empty) {
    const t = [...b];
    t[i] = "X";
    if (winnerOf(t) === "X") return i;
  }
  if (!b[4]) return 4;
  return empty[Math.floor(Math.random() * empty.length)];
}

const EMPTY = Array(9).fill(null);

export default function TicTacToeBoard() {
  const { lang } = useLang();
  const t = STR[lang];
  const [board, setBoard] = useState(EMPTY);
  const [thinking, setThinking] = useState(false);
  const [score, setScore] = useState({ x: 0, o: 0, d: 0 });
  const [round, setRound] = useState(0);
  const timer = useRef(null);

  const result = winnerOf(board);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [12, -12]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-14, 14]), { stiffness: 120, damping: 16 });

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!result) return;
    if (result === "X") sfx.win();
    else if (result === "O") sfx.lose();
    else sfx.draw();
    axios
      .post(`${API}/score`, { result: result === "X" ? "you" : result === "O" ? "ai" : "draw" })
      .catch(() => {});
    setScore((s) =>
      result === "X" ? { ...s, x: s.x + 1 } : result === "O" ? { ...s, o: s.o + 1 } : { ...s, d: s.d + 1 }
    );
    timer.current = setTimeout(() => {
      setBoard(EMPTY);
      setRound((r) => r + 1);
    }, 2200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const play = (i) => {
    if (board[i] || result || thinking) return;
    const next = [...board];
    next[i] = "X";
    setBoard(next);
    sfx.move();
    if (winnerOf(next)) return;
    setThinking(true);
    timer.current = setTimeout(() => {
      setBoard((b) => {
        if (winnerOf(b)) return b;
        const nb = [...b];
        nb[aiMove(nb)] = "O";
        return nb;
      });
      sfx.ai();
      setThinking(false);
    }, 550);
  };

  const reset = () => {
    clearTimeout(timer.current);
    setBoard(EMPTY);
    setThinking(false);
    setScore({ x: 0, o: 0, d: 0 });
    setRound((r) => r + 1);
  };

  const status = result
    ? result === "X"
      ? { text: t.st_win, cls: "text-sky-300" }
      : result === "O"
        ? { text: t.st_lose, cls: "text-amber-300" }
        : { text: t.st_draw, cls: "text-slate-300" }
    : thinking
      ? { text: t.st_thinking, cls: "text-slate-400" }
      : { text: t.st_your_turn, cls: "text-slate-300" };

  return (
    <div
      data-testid="interactive-game-board"
      className="relative"
      style={{ perspective: 1200 }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
      }}
      onMouseLeave={() => {
        mx.set(0.5);
        my.set(0.5);
      }}
    >
      <div className="absolute -inset-10 bg-[radial-gradient(circle_at_50%_40%,rgba(46,107,255,0.18),rgba(251,191,36,0.1)_45%,transparent_70%)] blur-2xl pointer-events-none" />

      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative"
      >
        <div className="rounded-[2rem] border border-white/10 bg-[#0c1122]/90 backdrop-blur-xl p-5 sm:p-7 shadow-[0_40px_120px_-20px_rgba(46,107,255,0.35)]">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <span className="grid place-items-center w-8 h-8 rounded-lg bg-sky-400/15 border border-sky-400/30">
                <User className="w-4 h-4 text-sky-300" />
              </span>
              <div>
                <p className="text-xs font-bold text-white leading-none">{t.you}</p>
                <p className="mono text-[10px] text-sky-300 mt-1">{score.x} {t.wins}</p>
              </div>
            </div>
            <span className="mono text-[10px] uppercase tracking-[0.3em] text-slate-500">vs</span>
            <div className="flex items-center gap-2">
              <div className="text-right">
                <p className="text-xs font-bold text-white leading-none">DUELO IA</p>
                <p className="mono text-[10px] text-amber-300 mt-1">{score.o} {t.wins}</p>
              </div>
              <span className="grid place-items-center w-8 h-8 rounded-lg bg-amber-400/15 border border-amber-400/30">
                <Bot className="w-4 h-4 text-amber-300" />
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2.5 sm:gap-3" key={round}>
            {board.map((cell, i) => (
              <motion.button
                key={`${round}-${i}`}
                data-testid={`board-cell-${i}`}
                onClick={() => play(i)}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.9 + i * 0.05, duration: 0.4 }}
                whileHover={!cell && !result ? { scale: 1.05, backgroundColor: "rgba(56,189,248,0.08)" } : {}}
                whileTap={!cell && !result ? { scale: 0.92 } : {}}
                className="aspect-square rounded-2xl border border-white/10 bg-white/[0.03] grid place-items-center cursor-pointer transition-colors duration-200"
                aria-label={`Célula ${i + 1}`}
              >
                <AnimatePresence>
                  {cell && (
                    <motion.span
                      initial={{ scale: 0, rotate: -90 }}
                      animate={{ scale: 1, rotate: 0 }}
                      exit={{ scale: 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 18 }}
                      className={`font-display text-4xl sm:text-5xl font-black ${
                        cell === "X" ? "text-sky-300 text-glow-blue" : "text-amber-400 text-glow-gold"
                      }`}
                    >
                      {cell}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>
            ))}
          </div>

          <div className="mt-5 flex items-center justify-between">
            <p data-testid="board-status" className={`mono text-[11px] uppercase tracking-[0.15em] ${status.cls}`}>
              {status.text}
            </p>
            <button
              onClick={reset}
              data-testid="board-reset-btn"
              className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-slate-400 hover:text-amber-300 hover:border-amber-400/40 hover:rotate-[-120deg] transition-all duration-300"
              aria-label="Reiniciar partida"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          className="animate-float-slow absolute -top-6 -right-4 sm:-right-8 rounded-2xl border border-amber-400/30 bg-[#141b33]/95 backdrop-blur px-4 py-2.5 shadow-xl"
          style={{ "--float-rot": "4deg", transform: "translateZ(60px)" }}
        >
          <p className="mono text-[10px] uppercase tracking-widest text-amber-300">{t.in_dispute}</p>
          <p className="font-display font-bold text-white text-sm">{t.credits10}</p>
        </div>
        <div
          className="animate-float-slow absolute -bottom-6 -left-4 sm:-left-8 rounded-2xl border border-sky-400/30 bg-[#141b33]/95 backdrop-blur px-4 py-2.5 shadow-xl"
          style={{ "--float-rot": "-5deg", animationDelay: "1.2s", transform: "translateZ(60px)" }}
        >
          <p className="mono text-[10px] uppercase tracking-widest text-sky-300">{t.match}</p>
          <p className="font-display font-bold text-white text-sm">{t.s60}</p>
        </div>
      </motion.div>
    </div>
  );
}
